import { useState } from 'react';
import { CustomModal } from '@/components/CustomModal/CustomModal';
import { ResponsiveTable } from '@/components/Table/ResponsiveTable/ResponsiveTable';
import { GoldenTag } from '@/reports/shared/DataCard/DataCardTag/DataCardTag';
import { EmptyVehicleSectionDisclaimer } from '@/reports/shared/ReportSection/EmptyVehicleSectionDisclaimer/EmptyVehicleSectionDisclaimer';
import { ReportSection } from '@/reports/shared/ReportSection/ReportSection';
import {
  AutomobileTitleRegistrations,
  AutomobileTitleRegistration,
  StatesContact,
  StateContact,
} from '@/reports/types/vehicles';
import { handleClickStateContact } from '@/reports/vehicle/VehicleReport';
import { getHowItWorksContent } from '@/reports/vehicle/utils';
import { PureTextButton, Text } from '@/theme/index';
import { commaFormat } from '@/utils/commaFormat/commaFormat';
import { formatDateFromReport } from '@/utils/formatDateFromReport/formatDateFromReport';
import { increment } from '@/utils/increment';
import { useScreenSize } from '@/utils/useScreenSize';
import { StyledDataCard } from './components';

export interface VehicleTitleRegistrationsProps {
  /** A list of all title registration data */
  vehicleTitleRegistrationsList: AutomobileTitleRegistrations;
  statesContact: StatesContact;
}

/**
 * This component is responsible for providing users with the ability to see all the vehicle title registrations.
 * Receive a list of vehicle title registrations.
 */

export const VehicleTitleRecords = ({
  vehicleTitleRegistrationsList,
  statesContact,
}: VehicleTitleRegistrationsProps) => {
  const { isMobile } = useScreenSize();
  const [openModal, setOpenModal] = useState(false);
  const [stateContactSelected, setStateContactSelected] = useState<
    StateContact | undefined
  >(undefined);
  const howItWorksContent = getHowItWorksContent('Title Records');

  const header = ['Title Issue Date', 'State', 'Status', 'Mileage', 'VIN'].map(
    (text, index) => (
      <Text key={index} variant={isMobile ? 'body2' : 'h5'} sx={{ p: 1, m: 0 }}>
        {text}
      </Text>
    )
  );

  const modalStateContactTitle = stateContactSelected && (
    <Text variant="h3">{`How to Request a State Vehicle Record: ${stateContactSelected.name}`}</Text>
  );

  const modalStateContactDescription = stateContactSelected && (
    <div
      dangerouslySetInnerHTML={{
        __html: stateContactSelected?.instructions || '',
      }}
    />
  );

  function handleCloseStateModal() {
    setOpenModal(!openModal);
  }

  const extractTitleRecordData = (title: AutomobileTitleRegistration) => {
    const mainTitle = [
      <Text variant="body2" sx={{ p: 1 }}>
        {formatDateFromReport(title.date?.full) || ''}
      </Text>,
      <PureTextButton
        variant="text"
        color="success"
        sx={{
          p: 1,
          '&:hover': {
            textDecoration: 'underline',
          },
        }}
        onClick={() => {
          handleClickStateContact(
            title?.issuing_authority || '',
            statesContact,
            setStateContactSelected,
            handleCloseStateModal
          );
        }}
      >
        {title?.issuing_authority || ''}
      </PureTextButton>,
      <Text variant="body2" sx={{ p: 1 }}>
        Current
      </Text>,
      <Text variant="body2" sx={{ p: 1 }}>
        {title?.odometer ? `${commaFormat(title?.odometer)}` : '-'}
      </Text>,
      <Text variant="body2" sx={{ p: 1 }}>
        {title?.vin || ''}
      </Text>,
    ];

    const historyItems = title.history.map((historyItem) => [
      <Text variant="body2" sx={{ p: 1 }}>
        {formatDateFromReport(historyItem?.date?.full) || ''}
      </Text>,
      <PureTextButton
        variant="text"
        color="success"
        sx={{
          p: 1,
          '&:hover': {
            textDecoration: 'underline',
          },
        }}
        onClick={() => {
          handleClickStateContact(
            historyItem?.issuing_authority || '',
            statesContact,
            setStateContactSelected,
            handleCloseStateModal
          );
        }}
      >
        {historyItem?.issuing_authority || ''}
      </PureTextButton>,
      <Text variant="body2" sx={{ p: 1 }}>
        History
      </Text>,
      <Text variant="body2" sx={{ p: 1 }}>
        {historyItem?.odometer ? `${commaFormat(historyItem?.odometer)}` : '-'}
      </Text>,
      <Text variant="body2" sx={{ p: 1 }}>
        {historyItem?.vin || ''}
      </Text>,
    ]);

    return [mainTitle, ...historyItems];
  };

  const modifiedTitleRecordsComponents = vehicleTitleRegistrationsList.flatMap(
    (title, index) => {
      const data = extractTitleRecordData(title);

      return (
        <StyledDataCard
          tag={
            <GoldenTag>
              <Text fontWeight="bold">Premium</Text>
            </GoldenTag>
          }
          key={index}
        >
          <Text variant="h2">{`Title ${increment(index)}`}</Text>
          <Text
            sx={{
              color: (theme) => theme.palette.text.secondary,
              fontSize: '0.75rem',
              fontWeight: 'bold',
              mb: { sm: 2 },
            }}
          >
            Source: NMVTIS
          </Text>
          <ResponsiveTable
            tableData={data}
            headers={header}
            sectionId={`vehicle-title-registration-${index}`}
          />
        </StyledDataCard>
      );
    }
  );

  return (
    <ReportSection
      title="Title Records"
      showTopBar
      hasContent={vehicleTitleRegistrationsList.length > 0}
      sectionId="vehicle-title-records"
      howItWorks={howItWorksContent}
      customEmptyText={<EmptyVehicleSectionDisclaimer title="title records" />}
    >
      <CustomModal
        open={openModal}
        customTitle={modalStateContactTitle}
        description={modalStateContactDescription}
        returnButtonText="Return to vehicle report"
        handleClose={handleCloseStateModal}
      />
      {modifiedTitleRecordsComponents}
    </ReportSection>
  );
};
